import type { HeaderSchemaType } from '@vedalib/editor/lib/brand'

import { TooltipCourse } from 'components/Editor/EditorElements/courseKit/DropDownCourse'
import type { ElementStyleCss } from 'services/Branding/types'
import { useProjectT } from 'services/ProjectLabels'
import { testPropsEl } from 'utils/test/qaData'

import BurgerStyled from '../../styled/BurgerStyled'
import LineStyled from '../../styled/LineStyled'
import { CustomMenuStyled, MenuOpenIconStyled } from './styled'

interface IMenuOpenIconProps {
  toggleSidebar: () => void
  isDisabledSidebarAndHome?: boolean
  styles: ElementStyleCss<HeaderSchemaType>
  isFloatingBurger?: boolean
  positionRight?: boolean
  isOpen?: boolean
}

const ANIMATION_VARIANTS = {
  open: {
    top: {
      top: 13,
      rotate: 45,
      transition: { top: { duration: 0.3 }, rotate: { duration: 0.3, delay: 0.3 } },
    },
    middle: { top: 13, opacity: 0, transition: { opacity: { duration: 0.3 } } },
    bottom: {
      width: 15,
      top: 13,
      rotate: -45,
      transition: { top: { duration: 0.3 }, rotate: { duration: 0.3, delay: 0.3 } },
    },
  },
  closed: {
    top: {
      top: 8,
      rotate: 0,
      transition: { top: { duration: 0.3, delay: 0.3 }, rotate: { duration: 0.3 } },
    },
    middle: { top: 13, opacity: 1, transition: { opacity: { duration: 0.3, delay: 0.3 } } },
    bottom: {
      width: 8,
      top: 18,
      rotate: 0,
      transition: { top: { duration: 0.3, delay: 0.3 }, rotate: { duration: 0.3 } },
    },
  },
}

const MenuOpenIcon: React.FC<IMenuOpenIconProps> = ({
  toggleSidebar,
  isDisabledSidebarAndHome: disabled,
  styles,
  isFloatingBurger,
  positionRight,
  isOpen,
}) => {
  const pt = useProjectT()
  const burgerIconBg = styles.burgerIcon.backgroundImage
  const closeIconBg = styles.crossIcon.backgroundImage
  const showCustomIcon = (burgerIconBg && !isOpen) || (closeIconBg && isOpen)

  return (
    <TooltipCourse
      disabled={disabled}
      overlay={isOpen ? pt('header.courseContentHide') : pt('header.courseContent')}
    >
      <MenuOpenIconStyled
        $isFloatingBurger={isFloatingBurger}
        $positionRight={positionRight}
        $styles={styles}
        aria-expanded={isOpen}
        aria-label={isOpen ? pt('header.courseContentHide') : pt('header.courseContent')}
        disabled={disabled}
        {...testPropsEl('button', { name: 'openCourseSidebar' })}
        onClick={() => !disabled && toggleSidebar()}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            toggleSidebar()
          }
          if (e.key === 'Escape' && isOpen) {
            e.preventDefault()
            toggleSidebar()
          }
        }}
        style={isFloatingBurger ? styles.effects : {}}
      >
        {showCustomIcon ? (
          <CustomMenuStyled style={{ backgroundImage: isOpen ? closeIconBg : burgerIconBg }} />
        ) : (
          <BurgerStyled $disabled={disabled} $styles={styles} aria-hidden>
            <LineStyled
              $disabled={disabled}
              $styles={styles}
              animate={isOpen ? ANIMATION_VARIANTS.open.top : ANIMATION_VARIANTS.closed.top}
              initial={false}
            />
            <LineStyled
              $disabled={disabled}
              $styles={styles}
              animate={isOpen ? ANIMATION_VARIANTS.open.middle : ANIMATION_VARIANTS.closed.middle}
              initial={false}
            />
            <LineStyled
              $disabled={disabled}
              $styles={styles}
              animate={isOpen ? ANIMATION_VARIANTS.open.bottom : ANIMATION_VARIANTS.closed.bottom}
              initial={false}
            />
          </BurgerStyled>
        )}
      </MenuOpenIconStyled>
    </TooltipCourse>
  )
}

export default MenuOpenIcon
